import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FormattedMessage } from '../../../util/reactIntl';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { getShipByLabel } from '../../../util/orderStatusGroups';
import { ExternalLink } from '../../../components';
import Print from '../Print';

import css from './ShipOrderSection.module.css';

const { UUID } = sdkTypes;

const toLabel = value => {
  if (!value) {
    return null;
  }
  if (typeof value === 'object') {
    return value.label || value.values || null;
  }
  return String(value);
};

// Brand is saved under a different key per category
const BRAND_KEYS = [
  'brandOption',
  'brand',
  'dancewear_brandOption',
  'dancewear_brand',
  'costume_brandOption',
  'costume_brand',
  'shoe_brandOption',
  'shoe_brand',
];
const getBrand = publicData => toLabel(BRAND_KEYS.map(key => publicData[key]).find(Boolean));

// Brand, SKU and size come from each item's listing (cart items don't carry them all). Sold
// listings are closed, which listings.show still returns.
const fetchItemDetails = listingIds => (dispatch, getState, sdk) =>
  !sdk?.listings
    ? Promise.resolve({})
    : Promise.all(
        listingIds.map(id =>
          sdk.listings
            .show({ id: new UUID(id), 'fields.listing': ['publicData'] })
            .then(response => [id, response?.data?.data?.attributes?.publicData || {}])
            .catch(() => [id, null])
        )
      ).then(entries => Object.fromEntries(entries.filter(([, publicData]) => publicData)));

const withDetails = (items, detailsById) =>
  items.map(item => {
    const publicData = (item.listingId && detailsById[item.listingId]) || {};
    return {
      ...item,
      brand: item.brand || getBrand(publicData),
      sku: item.sku || publicData.sku || null,
      size: item.size || toLabel(publicData.sizeOption || publicData.sizeLabel || publicData.size),
    };
  });

/**
 * The seller's steps for an order waiting to ship: pack the items (with a packing slip), label
 * it the way this order ships, then mark it shipped. "Ship order" on the orders list opens the
 * order at this section (#ship).
 */
const ShipOrderSection = props => {
  const {
    transaction,
    items,
    printProps,
    isOwnShipping,
    downloadLabelUrl,
    qrCodeUrl,
    showLabelUnavailable,
    carrierName,
    serviceLevelName,
    shipButtons,
  } = props;
  const dispatch = useDispatch();
  const [detailsById, setDetailsById] = useState({});
  const listingIds = Array.from(new Set(items.map(item => item.listingId).filter(Boolean)));
  const listingIdsKey = listingIds.join(',');

  useEffect(() => {
    if (!listingIdsKey) {
      return;
    }
    let cancelled = false;
    dispatch(fetchItemDetails(listingIdsKey.split(','))).then(
      details => !cancelled && setDetailsById(details)
    );
    return () => {
      cancelled = true;
    };
  }, [dispatch, listingIdsKey]);

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hash === '#ship') {
      document.getElementById('ship')?.scrollIntoView({ block: 'start' });
    }
  }, []);

  const detailedItems = withDetails(items, detailsById);
  const itemCount = detailedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const shipBy = getShipByLabel(transaction);

  return (
    <section id="ship" className={css.root}>
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="ShipOrderSection.title" />
        </h2>
        <span className={css.shipBy}>{shipBy}</span>
      </div>

      <div className={css.step}>
        <h3 className={css.stepTitle}>
          <span className={css.stepNumber}>1</span>
          <FormattedMessage id="ShipOrderSection.packTitle" values={{ count: itemCount }} />
        </h3>
        <ul className={css.items}>
          {detailedItems.map((item, index) => (
            <li key={`${item.listingId || item.title}-${index}`} className={css.item}>
              {item.firstImage ? (
                <img className={css.itemImage} src={item.firstImage} alt="" />
              ) : (
                <span className={css.itemImage} />
              )}
              <span className={css.itemText}>
                <span className={css.itemTitle}>
                  {item.quantity > 1 ? `${item.quantity} × ` : ''}
                  {item.title}
                </span>
                <span className={css.itemDetails}>
                  {[item.brand, item.size, item.sku ? `SKU ${item.sku}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
            </li>
          ))}
        </ul>
        <div className={css.packingSlip}>
          <Print {...printProps} savedCart={detailedItems} />
        </div>
      </div>

      <div className={css.step}>
        <h3 className={css.stepTitle}>
          <span className={css.stepNumber}>2</span>
          <FormattedMessage id="ShipOrderSection.labelTitle" />
        </h3>
        {isOwnShipping ? (
          <p className={css.stepText}>
            <FormattedMessage id="ShipOrderSection.ownLabel" />
          </p>
        ) : (
          <>
            {serviceLevelName || carrierName ? (
              <p className={css.stepText}>
                <FormattedMessage
                  id="ShipOrderSection.prepaidLabel"
                  values={{ service: serviceLevelName || carrierName }}
                />
              </p>
            ) : null}
            {downloadLabelUrl || qrCodeUrl ? (
              <div className={css.labelButtons}>
                {downloadLabelUrl ? (
                  <ExternalLink className={css.primaryLink} href={downloadLabelUrl}>
                    <FormattedMessage id="ShipOrderSection.printLabel" />
                  </ExternalLink>
                ) : null}
                {qrCodeUrl ? (
                  <ExternalLink className={css.secondaryLink} href={qrCodeUrl}>
                    <FormattedMessage id="ShipOrderSection.showQrCode" />
                  </ExternalLink>
                ) : null}
              </div>
            ) : null}
            {showLabelUnavailable ? (
              <p className={css.stepText}>
                <FormattedMessage id="TransactionPanel.shippingLabelUnavailable" />
              </p>
            ) : null}
            {carrierName ? (
              <p className={css.dropOff}>
                <FormattedMessage id="ShipOrderSection.dropOff" values={{ carrier: carrierName }} />
              </p>
            ) : null}
          </>
        )}
      </div>

      <div className={css.step}>
        <h3 className={css.stepTitle}>
          <span className={css.stepNumber}>3</span>
          <FormattedMessage id="ShipOrderSection.sendTitle" />
        </h3>
        <p className={css.stepText}>
          <FormattedMessage id="ShipOrderSection.sendText" />
        </p>
        {shipButtons}
      </div>
    </section>
  );
};

export default ShipOrderSection;
